import React from 'react';
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useLiveData, useCountdown } from '../hooks/useLiveData';
import { getKeyStats, getSignupData, getAgentLogs, isConfigured } from '../lib/api';
import SetupBanner from '../components/SetupBanner';
import { morningPulse as mockPulse, signupData as mockSignups, agents as mockAgents } from '../data/mockData';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '6px', padding: '8px 12px' }}>
        <div style={{ fontSize: '10px', color: 'var(--text-3)', marginBottom: '4px' }}>{label}</div>
        {payload.map((p, i) => <div key={i} style={{ fontSize: '12px', color: p.color }}>{p.name}: {p.value}</div>)}
      </div>
    );
  }
  return null;
};

function LiveDot({ status }) {
  const color = status === 'GREEN' ? 'var(--green)' : status === 'AMBER' ? 'var(--yellow)' : 'var(--red)';
  return <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: color, boxShadow: `0 0 8px ${color}`, marginRight: '8px', animation: 'pulse 2s infinite' }} />;
}

export default function Dashboard() {
  const configured = isConfigured();
  const countdown = useCountdown('2026-05-20T00:00:00');

  const mockStats = { ...mockPulse.stats, system_status: mockPulse.status, summary: mockPulse.summary, generated_at: new Date().toISOString(), pending_approvals: 3 };

  const { data: stats, loading: statsLoading, lastUpdated } = useLiveData(
    configured ? getKeyStats : () => Promise.resolve(mockStats), 30000
  );
  const { data: signups, loading: signupsLoading } = useLiveData(
    configured ? getSignupData : () => Promise.resolve(mockSignups), 60000
  );
  const { data: agentLogs } = useLiveData(
    configured
      ? getAgentLogs
      : () => Promise.resolve(mockAgents.flatMap(a => a.logs.map(l => ({ ...l, agent_name: a.name, platform: a.platform, ran_at: new Date().toISOString() })))),
    30000
  );

  const s = stats || mockStats;

  return (
    <div>
      {!configured && <SetupBanner />}

      {/* Countdown — live ticking every second */}
      <div className="countdown-card">
        <div>
          <div style={{ fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--accent-mmf)', marginBottom: '4px' }}>🚀 UCAT Independence Day</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '18px', fontWeight: '800', color: 'var(--text)' }}>May 20th Launch</div>
          <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px' }}>Phase 2: Hype Waitlist · Get ready</div>
        </div>
        <div className="countdown-nums">
          {[['Days', countdown.days], ['Hours', countdown.hours], ['Mins', countdown.mins], ['Secs', countdown.secs]].map(([unit, val], i) => (
            <React.Fragment key={unit}>
              {i > 0 && <div style={{ color: 'var(--text-3)', fontFamily: 'var(--font-display)', fontSize: '28px', fontWeight: '300', paddingTop: '2px' }}>:</div>}
              <div className="countdown-unit">
                <div className="countdown-n">{String(val).padStart(2, '0')}</div>
                <div className="countdown-u">{unit}</div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Morning Pulse */}
      <div className="pulse-card">
        <div className="pulse-icon">
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="1,8 4,8 5,3 7,13 9,5 11,10 12,8 15,8" /></svg>
        </div>
        <div style={{ flex: 1 }}>
          <div className="pulse-label">
            ☀ Chief of Staff — Morning Pulse
            {lastUpdated && <span style={{ fontSize: '10px', color: 'var(--text-3)', marginLeft: '8px' }}>
              · refreshed {Math.floor((new Date() - lastUpdated) / 1000)}s ago
            </span>}
          </div>
          <div className="pulse-text">{statsLoading ? 'Loading...' : s.summary}</div>
          <div className="pulse-time">
            <LiveDot status={s.system_status} />
            System {s.system_status}
            {s.generated_at && ` · Generated ${new Date(s.generated_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`}
            {!configured && ' · (demo data)'}
          </div>
        </div>
      </div>

      {/* Key Stats — refresh every 30s */}
      <div className="grid-4" style={{ marginBottom: '24px' }}>
        {[
          { label: 'New Signups (24h)', value: s.new_signups?.toLocaleString() || '0', sub: 'Both platforms', cls: '' },
          { label: 'Active Users', value: s.active_users?.toLocaleString() || '0', sub: 'Today', cls: 'mmf' },
          { label: 'Agents Running', value: `${s.agents_running || 0}/11`, sub: 'Live agents', cls: 'gold' },
          { label: 'Emails Sent (24h)', value: s.emails_sent?.toLocaleString() || '0', sub: '£0 · Resend', cls: 'white' },
        ].map(({ label, value, sub, cls }) => (
          <div key={label} className={`stat-card ${cls}`}>
            <div className="stat-label">{label}</div>
            <div className="stat-value">{statsLoading ? '—' : value}</div>
            <div className="stat-sub">{sub}</div>
          </div>
        ))}
      </div>

      {/* Charts + live log */}
      <div className="grid-2" style={{ marginBottom: '24px' }}>
        <div className="card">
          <div className="section-label">Daily Signups{!configured && <span style={{ color: 'var(--text-3)', fontWeight: 400 }}> · demo</span>}</div>
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={signupsLoading ? [] : (signups || [])}>
              <defs>
                <linearGradient id="gM" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00d4aa" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#00d4aa" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gF" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ff6b35" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#ff6b35" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" tick={{ fill: 'var(--text-3)', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="meduvita" name="Meduvita" stroke="#00d4aa" strokeWidth={2} fill="url(#gM)" />
              <Area type="monotone" dataKey="mmf" name="MMF" stroke="#ff6b35" strokeWidth={2} fill="url(#gF)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <div className="section-label">Live Agent Feed <span style={{ color: 'var(--green)', fontSize: '9px' }}>● live</span></div>
          <div style={{ maxHeight: '175px', overflowY: 'auto' }}>
            {(agentLogs || []).slice(0, 10).map((log, i) => (
              <div key={i} className="log-entry">
                <span className="log-time" style={{ minWidth: '44px', fontSize: '10px' }}>
                  {new Date(log.ran_at || Date.now()).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span className={`platform-tag ${log.platform}`} style={{ fontSize: '8px', padding: '1px 5px', marginRight: '5px' }}>{log.platform}</span>
                <span className={`log-msg ${log.status === 'error' ? 'error' : log.status === 'warn' || log.status === 'flagged' ? 'warn' : 'ok'}`} style={{ fontSize: '11px' }}>
                  {log.agent_name}: {log.message}
                </span>
              </div>
            ))}
            {(!agentLogs || agentLogs.length === 0) && (
              <div style={{ fontSize: '12px', color: 'var(--text-3)', padding: '20px 0', textAlign: 'center' }}>No agent runs yet. Trigger one from GitHub Actions.</div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid-3">
        <div className="card">
          <div className="section-label">Approval Queue</div>
          {(s.pending_approvals || 0) === 0
            ? <div style={{ fontSize: '12px', color: 'var(--text-3)', padding: '12px 0' }}>Queue empty — nothing waiting.</div>
            : <div style={{ fontSize: '24px', fontFamily: 'var(--font-display)', fontWeight: '800', color: 'var(--yellow)', padding: '8px 0' }}>{s.pending_approvals} pending</div>
          }
          <div style={{ fontSize: '11px', color: 'var(--accent-m)', cursor: 'pointer', marginTop: '4px' }}>Review & approve →</div>
        </div>

        <div className="card">
          <div className="section-label">System Status</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0' }}>
            <LiveDot status={s.system_status} />
            <span style={{ fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: '800', color: 'var(--text)' }}>{s.system_status}</span>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-3)' }}>
            {s.system_status === 'GREEN' && 'All agents nominal.'}
            {s.system_status === 'AMBER' && 'One or more agents flagged.'}
            {s.system_status === 'RED' && 'Agent errors — check GitHub Actions.'}
          </div>
        </div>

        <div className="card">
          <div className="section-label">Launch Checklist</div>
          {[
            { label: 'Architecture & DB schema', done: true },
            { label: 'AI agents deployed', done: true },
            { label: 'Command Center live', done: true },
            { label: 'Real data connected', done: configured },
            { label: 'Hype waitlist open', done: false },
            { label: 'May 20th launch 🚀', done: false },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ width: '14px', height: '14px', borderRadius: '3px', flexShrink: 0, background: item.done ? 'var(--green)' : 'transparent', border: item.done ? 'none' : '1px solid var(--border-bright)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {item.done && <svg width="8" height="8" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2"><polyline points="2,6 5,9 10,3" /></svg>}
              </div>
              <span style={{ fontSize: '11px', color: item.done ? 'var(--text-2)' : 'var(--text-3)', textDecoration: item.done ? 'line-through' : 'none' }}>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
